import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crown, Loader2, Lock } from "lucide-react";
import type { ChampionTeamDto } from "@/types/api";
import { championApi } from "@/api/champion";
import { useScope } from "@/app/useScope";
import { TeamFlag } from "@/components/TeamFlag";
import { RequireContext, ErrorState, PageLoader, EmptyState } from "@/components/StateViews";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export function ChampionPickPage() {
  return (
    <RequireContext>
      <ChampionPick />
    </RequireContext>
  );
}

function ChampionPick() {
  const scope = useScope();
  const qc = useQueryClient();
  const [choice, setChoice] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["champion", scope.leagueId, scope.tournamentId],
    queryFn: () => championApi.get(scope),
  });

  const submit = useMutation({
    mutationFn: (teamId: string) => championApi.submit({ ...scope, teamId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["champion", scope.leagueId, scope.tournamentId] });
      setChoice(null);
    },
  });

  if (q.isLoading) return <PageLoader />;
  if (q.error) return <ErrorState error={q.error} />;
  const view = q.data!;
  const deadline = view.deadline ? new Date(view.deadline).toLocaleString("hu-HU", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : null;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <Crown className="size-5 text-warning" /> Bajnoki tipp
        </h1>
        <p className="text-sm text-muted-foreground">
          Tippeld meg a torna győztesét. Egyszer adható le, <span className="font-medium">nem módosítható</span>.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 text-sm">
        {view.bonusPoints > 0 && <Badge>Bajnok-bónusz: +{view.bonusPoints}</Badge>}
        {view.earlyBonusPoints > 0 && <Badge variant="warning">Korai (Látnok) bónusz: +{view.earlyBonusPoints}</Badge>}
        {deadline && <Badge variant="outline">Határidő: {deadline}</Badge>}
      </div>

      {view.myTeamId ? (
        <div className="rounded-xl border border-warning/50 bg-warning/10 p-4">
          <div className="flex items-center gap-3">
            <TeamFlag name={view.myTeamName ?? ""} flag={view.myTeamFlag} className="size-9" />
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">A te bajnok-tipped</div>
              <div className="text-lg font-semibold">{view.myTeamName}</div>
            </div>
            <Lock className="ml-auto size-4 text-muted-foreground" />
          </div>
        </div>
      ) : view.locked ? (
        <EmptyState title="A bajnoki tipp lezárult" description="Az első meccs / a határidő után már nem adható le bajnoki tipp." icon={<Lock className="size-7" />} />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Válaszd ki a győztesnek várt csapatot, majd erősítsd meg.</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {view.teams.map((t) => (
              <TeamOption key={t.id} team={t} selected={choice === t.id} onSelect={() => setChoice(t.id)} />
            ))}
          </div>
          {submit.error && <p className="text-sm text-destructive">{(submit.error as Error).message}</p>}
          <div className="flex items-center gap-2">
            <Button disabled={!choice || submit.isPending} onClick={() => choice && submit.mutate(choice)}>
              {submit.isPending && <Loader2 className="size-4 animate-spin" />}
              Bajnok-tipp véglegesítése
            </Button>
            {choice && <span className="text-sm text-muted-foreground">Kiválasztva: {view.teams.find((t) => t.id === choice)?.name}</span>}
          </div>
        </div>
      )}

      {view.othersVisible && view.otherPicks.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">Mások bajnok-tippjei</h2>
          <ul className="divide-y divide-border/60 rounded-xl border border-border">
            {view.otherPicks.map((p, i) => (
              <li key={i} className="flex items-center gap-2 px-3 py-2 text-sm">
                <TeamFlag name={p.teamName} flag={p.flag} className="size-5" />
                <span className="font-medium">{p.teamName}</span>
                <span className="ml-auto truncate text-muted-foreground">{p.userName}</span>
                {p.bonusAwarded && <Badge variant="warning">✓</Badge>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TeamOption({ team, selected, onSelect }: { team: ChampionTeamDto; selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex items-center gap-2 rounded-lg border p-2.5 text-left text-sm font-medium transition-colors",
        selected ? "border-warning bg-warning/10 text-warning" : "border-border hover:border-primary/50",
      )}
    >
      <TeamFlag name={team.name} flag={team.flag} className="size-6" />
      <span className="min-w-0 flex-1 truncate">{team.name}</span>
    </button>
  );
}
